import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Divider } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { roundToDecimals } from 'utils/number';

import {
  selectCommonInfoLoanApplication,
  selectIsChattelLoanFromPropertyAndLoanSummary,
  selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary,
  selectIsLoanPurposeRefinanceFromPropertyAndLoanSummary,
  selectIsPropertyEnrichmentLoading,
  selectPropertyEnrichmentData,
} from 'store/loanApplications/selectors';
import { getImpoundTypesAction } from 'store/system/actions';

import {
  defaultForm,
  defaultInitialEntitySideData,
  pagesPropTypes,
} from '../MainContent.constants';
import EnrichmentFieldIndicators from '../Property/components/EnrichmentFieldIndicators';
import { getPropertyExpensesEnrichmentValuesMap } from '../Property/enrichmentFieldMapping';

import OtherExpenses from './OtherExpenses';
import {
  getExistingPropertyExpensesConfig,
  getProposedPropertyExpensesConfig,
} from './PropertyExpenses.config';
import { boxStyle } from './PropertyExpenses.styles';
import { checkIsAmountOfRentCollectedActive } from './PropertyExpenses.utils';
import PropertyTaxes from './PropertyTaxes';
import ProposedPrincipalAndInterestTooltip from './ProposedPrincipalAndInterestTooltip';
import SupplementalInsurance from './SupplementalInsurance';

const PropertyExpenses = ({
  changedFields,
  isLoading,
  isSaving,
  form = defaultForm,
  validationErrors,
  validateField,
  handleChange,
  setEntityConfig,
  setInForm,
  className,
  setIsLoading,
  applicationData,
  staticValidateField,
  pendingChanges,
  disabledPendingChangesHighlight,
  setEntitySideData,
  setInitialEntitySideData,
  entitySideData,
  initialEntitySideData = defaultInitialEntitySideData,
  setIsSavingAllowed,
  setIsNestedEntityEditActive,
}) => {
  const dispatch = useDispatch();
  const formContainerRef = useRef(null);
  const enrichmentData = useSelector(selectPropertyEnrichmentData);
  const isEnrichmentLoading = useSelector(selectIsPropertyEnrichmentLoading);

  const enrichmentValuesMap = useMemo(
    () => getPropertyExpensesEnrichmentValuesMap(enrichmentData),
    [enrichmentData],
  );

  const isRefinance = useSelector(selectIsLoanPurposeRefinanceFromPropertyAndLoanSummary);
  const isAdditionalLoan = useSelector(selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary);
  const isHelocPurposePurchase = useSelector(
    selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  );
  const isChattel = useSelector(selectIsChattelLoanFromPropertyAndLoanSummary);
  const { loanPurposeType, loanPurpose } = useSelector(selectCommonInfoLoanApplication);

  const isFormDataEmpty = _.isEmpty(form);
  const entityData = isFormDataEmpty ? applicationData : (form ?? {});
  const entityProposedPropertyExpenses = entityData.proposedPropertyExpenses || {};

  const isPmiManualUpdate = entityProposedPropertyExpenses?.pmiManualUpdate;
  // LEN-156: a chattel loan only uses the property-tax and homeowners-insurance inputs here.
  const isExistingPropertyExpensesVisible =
    !isChattel && (isRefinance || (isAdditionalLoan && !isHelocPurposePurchase));

  // Read straight from the API payload: the qualifying P&I reaches applicationData but not the
  // config-driven form model. Subordinate HELOCs surface a qualifying P&I (note + program qualifying
  // rate) distinct from the note-rate P&I; compare at cents precision because the API returns an
  // unrounded P&I but a rounded qualifying value.
  const apiProposedPropertyExpenses = applicationData?.proposedPropertyExpenses;
  const proposedPrincipalAndInterest = apiProposedPropertyExpenses?.principalAndInterest;
  const qualifyingPrincipalAndInterest =
    apiProposedPropertyExpenses?.qualifyingPrincipalAndInterest;
  const isQualifyingPrincipalAndInterestShown =
    !_.isNil(qualifyingPrincipalAndInterest) &&
    !_.isNil(proposedPrincipalAndInterest) &&
    roundToDecimals(qualifyingPrincipalAndInterest, 2) !==
      roundToDecimals(proposedPrincipalAndInterest, 2);
  const { occupancy, propertyType } = useMemo(() => {
    return {
      occupancy: form.occupancy?.value || applicationData.occupancy,
      propertyType: form.propertyType?.value || applicationData.propertyType,
    };
  }, [
    form.occupancy?.value,
    form.propertyType?.value,
    applicationData.occupancy,
    applicationData.propertyType,
  ]);

  const isAmountOfRentCollectedActive = checkIsAmountOfRentCollectedActive({
    occupancy,
    propertyType,
  });

  useEffectSafeStrictMode(() => {
    dispatch(getImpoundTypesAction({ includeDeleted: true }));
  }, [dispatch, setIsLoading]);

  const proposedPrincipalAndInterestTitle = useMemo(
    () =>
      isQualifyingPrincipalAndInterestShown ? (
        <ProposedPrincipalAndInterestTooltip
          label="Principal & Interest"
          principalAndInterest={proposedPrincipalAndInterest}
          qualifyingPrincipalAndInterest={qualifyingPrincipalAndInterest}
        />
      ) : null,
    [
      isQualifyingPrincipalAndInterestShown,
      proposedPrincipalAndInterest,
      qualifyingPrincipalAndInterest,
    ],
  );

  const proposedPropertyExpensesConfig = useMemo(() => {
    return getProposedPropertyExpensesConfig({
      isPmiManualUpdate,
      isAmountOfRentCollectedActive,
      loanPurposeType,
      loanPurpose,
      isQualifyingPrincipalAndInterestShown,
      proposedPrincipalAndInterestTitle,
      isChattel,
    });
  }, [
    isAmountOfRentCollectedActive,
    isPmiManualUpdate,
    loanPurposeType,
    loanPurpose,
    isQualifyingPrincipalAndInterestShown,
    proposedPrincipalAndInterestTitle,
    isChattel,
  ]);

  const existingPropertyExpensesConfig = useMemo(() => {
    return getExistingPropertyExpensesConfig();
  }, []);

  useEffect(() => {
    if (!proposedPropertyExpensesConfig || !existingPropertyExpensesConfig) {
      return;
    }

    const combinedConfig = isExistingPropertyExpensesVisible
      ? {
          groups: [
            ...proposedPropertyExpensesConfig.groups,
            ...existingPropertyExpensesConfig.groups,
          ],
        }
      : proposedPropertyExpensesConfig;

    setEntityConfig(combinedConfig);
  }, [
    proposedPropertyExpensesConfig,
    existingPropertyExpensesConfig,
    setEntityConfig,
    isExistingPropertyExpensesVisible,
  ]);

  if (!proposedPropertyExpensesConfig) {
    return null;
  }

  return (
    <Box className={className}>
      <Box ref={formContainerRef} sx={boxStyle}>
        <EnrichmentFieldIndicators
          containerRef={formContainerRef}
          enrichmentValuesMap={enrichmentValuesMap}
          form={form}
          isLoading={isEnrichmentLoading}
          setInForm={setInForm}
          validateField={validateField}
        />
        <FormGenerator
          isNestedForm
          changedFields={changedFields}
          config={proposedPropertyExpensesConfig}
          disabledPendingChangesHighlight={disabledPendingChangesHighlight}
          form={form}
          formName={FormNames.PROPOSED_PROPERTY_EXPENSES}
          handleChange={handleChange}
          isLoading={isSaving || isLoading}
          numberOfColumns={4}
          pendingChanges={pendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />

        <div className="mt-24">
          <Divider />
        </div>

        <PropertyTaxes
          className={className}
          entitySideData={entitySideData}
          form={form}
          initialEntitySideData={initialEntitySideData}
          isLoading={isLoading}
          pendingChanges={pendingChanges}
          propertyTaxes={applicationData?.proposedPropertyExpenses?.propertyTaxes}
          setEntitySideData={setEntitySideData}
          setInitialEntitySideData={setInitialEntitySideData}
          setIsNestedEntityEditActive={setIsNestedEntityEditActive}
          setIsSavingAllowed={setIsSavingAllowed}
        />

        <SupplementalInsurance
          className={className}
          entitySideData={entitySideData}
          form={form}
          initialEntitySideData={initialEntitySideData}
          isHidden={isChattel}
          isLoading={isLoading}
          pendingChanges={pendingChanges}
          setEntitySideData={setEntitySideData}
          setInitialEntitySideData={setInitialEntitySideData}
          setIsNestedEntityEditActive={setIsNestedEntityEditActive}
          setIsSavingAllowed={setIsSavingAllowed}
          supplementalInsurances={applicationData?.proposedPropertyExpenses?.supplementalInsurances}
        />

        <OtherExpenses
          className={className}
          entitySideData={entitySideData}
          form={form}
          initialEntitySideData={initialEntitySideData}
          isHidden={isChattel}
          isLoading={isLoading}
          otherExpenses={applicationData?.proposedPropertyExpenses?.otherExpenses}
          pendingChanges={pendingChanges}
          setEntitySideData={setEntitySideData}
          setInitialEntitySideData={setInitialEntitySideData}
          setIsNestedEntityEditActive={setIsNestedEntityEditActive}
          setIsSavingAllowed={setIsSavingAllowed}
        />

        {isExistingPropertyExpensesVisible && (
          <FormGenerator
            isNestedForm
            changedFields={changedFields}
            config={existingPropertyExpensesConfig}
            disabledPendingChangesHighlight={disabledPendingChangesHighlight}
            form={form}
            formName={FormNames.EXISTING_PROPERTY_EXPENSES}
            handleChange={handleChange}
            isLoading={isSaving || isLoading}
            numberOfColumns={4}
            pendingChanges={pendingChanges}
            setInForm={setInForm}
            staticValidateField={staticValidateField}
            validateField={validateField}
            validationErrors={validationErrors}
          />
        )}
      </Box>
    </Box>
  );
};

PropertyExpenses.propTypes = pagesPropTypes;

export default memo(PropertyExpenses);
